import { useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { supabase } from "@/integrations/supabase/client";
import { isNative, isIOS } from "@/lib/platform";

const ONESIGNAL_APP_ID = "bbfbf693-5e21-4c1f-965e-bf31cf7694dd";
const LOG = (...args: any[]) => console.log("[OneSignalNative]", ...args);

export function useOneSignalNative() {
  const { isSignedIn, user } = useUser();

  useEffect(() => {
    if (!isNative) {
      LOG("skip: not native");
      return;
    }

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    const persist = async (subId?: string | null) => {
      LOG("persist called with subId:", subId, "user:", user?.id);
      if (cancelled || !subId || !user?.id) {
        LOG("persist skipped — missing subId or user");
        return;
      }
      try {
        const { error } = await supabase.rpc("save_push_subscription" as any, {
          _clerk_user_id: user.id,
          _onesignal_player_id: subId,
          _platform: isIOS ? "ios" : "android",
        });
        if (error) LOG("save_push_subscription ERROR:", error);
        else LOG("save_push_subscription OK for", subId);
      } catch (e) {
        LOG("persist threw:", e);
      }
    };

    (async () => {
      try {
        LOG("importing plugin…");
        const mod: any = await import(/* @vite-ignore */ "onesignal-cordova-plugin").catch((e) => {
          LOG("import FAILED:", e);
          return null;
        });
        const OneSignal: any = mod?.default ?? mod ?? (window as any).plugins?.OneSignal;
        if (!OneSignal) {
          LOG("OneSignal object NOT FOUND after import");
          return;
        }
        LOG("OneSignal loaded. keys:", Object.keys(OneSignal));

        try {
          OneSignal.initialize?.(ONESIGNAL_APP_ID);
          LOG("initialize called");
        } catch (e) {
          LOG("initialize threw:", e);
        }

        try {
          const perm = await OneSignal.Notifications?.requestPermission?.(true);
          LOG("requestPermission returned:", perm);
        } catch (e) {
          LOG("requestPermission threw:", e);
        }

        if (isSignedIn && user?.id) {
          try {
            OneSignal.login?.(user.id);
            LOG("login called with", user.id);
          } catch (e) {
            LOG("login threw:", e);
          }
        }

        const sub = OneSignal.User?.pushSubscription;
        LOG("pushSubscription object:", sub ? Object.keys(sub) : "NULL");
        let subId: string | null | undefined;
        try {
          subId = (await sub?.getIdAsync?.()) ?? sub?.id ?? null;
        } catch (e) {
          LOG("getIdAsync threw:", e);
          subId = sub?.id ?? null;
        }
        LOG("resolved subId:", subId);
        let granted = true;
        try {
          granted = (await OneSignal.Notifications?.getPermissionAsync?.()) !== false;
        } catch {
          /* assume granted if check unavailable */
        }
        if (granted) await persist(subId);

        const onChange = (event: any) => {
          LOG("subscription change event:", JSON.stringify(event));
          const id = event?.current?.id ?? event?.to?.id ?? event?.id ?? sub?.id ?? null;
          void persist(id);
        };
        try {
          sub?.addEventListener?.("change", onChange);
          cleanup = () => sub?.removeEventListener?.("change", onChange);
          LOG("change listener attached");
        } catch (e) {
          LOG("addEventListener threw:", e);
        }

        try {
          OneSignal.Notifications?.addEventListener?.("click", (event: any) => {
            const route = event?.notification?.additionalData?.route;
            if (route) window.location.href = route;
          });
        } catch (e) {
          LOG("click listener threw:", e);
        }
      } catch (e) {
        LOG("outer catch:", e);
      }
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [isSignedIn, user?.id]);
}
